import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, MapPin, Shield, Clock, Users, Navigation2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [mobileCompanyOpen, setMobileCompanyOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  const navLinks = [
    { name: 'Services', path: '/services' },
    { name: 'Track Shipment', path: '/track' },
    { name: 'Partnerships', path: '/partners' },
  ];

  const companyLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Drivers', path: '/deliver' },
    { name: 'Careers', path: '/careers' },
    { name: 'Legal & Policies', path: '/legal' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const isCompanyActive = companyLinks.some(l => isActive(l.path));

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCompanyOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setMobileCompanyOpen(false);
    setCompanyOpen(false);
  }, [location.pathname]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setCompanyOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setCompanyOpen(false), 150);
  };

  const trustItems = [
    { icon: Shield, text: 'Licensed & Insured' },
    { icon: Users, text: 'Professional Drivers' },
    { icon: Clock, text: 'On-Time Delivery Focus' },
    { icon: Navigation2, text: 'Serving the Southeast' },
  ];

  return (
    <>
      {/* Top utility strip */}
      <div className="bg-brand-surface/60 border-b border-white/5" data-testid="utility-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-1.5 text-xs">
            <a href="tel:224-374-7400" className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 transition-colors" data-testid="header-phone-link">
              <Phone className="w-3 h-3 text-brand-red/70" />
              <span>224-374-7400</span>
            </a>
            <div className="flex items-center gap-1.5 text-slate-500" data-testid="header-location">
              <MapPin className="w-3 h-3 text-brand-red/70" />
              <span>Based in Augusta, GA</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-brand-navy/80 border-b border-white/10" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="nav-logo">
              <img src="https://customer-assets.emergentagent.com/job_box-truck-delivery/artifacts/i3lweui9_image.png" alt="TopFleet Truck" className="h-9 w-auto" style={{ mixBlendMode: 'lighten', transform: 'scaleX(-1)' }} />
              <span className="font-heading text-xl font-black tracking-tight">
                <span style={{ color: '#DC2626' }}>TOP</span><span className="text-white">FLEET</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path}
                  className={`nav-link font-medium text-[15px] ${isActive(link.path) ? 'active' : ''}`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}-link`}>
                  {link.name}
                </Link>
              ))}

              {/* Company Dropdown */}
              <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} data-testid="company-dropdown">
                <button onClick={() => setCompanyOpen(prev => !prev)}
                  className={`nav-link font-medium text-[15px] flex items-center gap-1.5 ${isCompanyActive ? 'active' : ''}`}
                  data-testid="nav-company-trigger">
                  About Us
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${companyOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {companyOpen && (
                    <motion.div initial={{ opacity: 0, y: 8, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.96 }} transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-48 bg-brand-surface/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/40 overflow-hidden" data-testid="company-dropdown-menu">
                      <div className="py-1.5">
                        {companyLinks.map((link) => (
                          <Link key={link.path} to={link.path}
                            className={`block px-5 py-2.5 text-sm transition-colors ${isActive(link.path) ? 'text-white bg-white/5' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                            onClick={() => setCompanyOpen(false)}
                            data-testid={`nav-company-${link.name.toLowerCase().replace(/\s+/g, '-')}-link`}>
                            {link.name}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Contact CTA Button */}
              <Link to="/contact" className="btn-cta text-sm px-7 py-3" data-testid="nav-quote-button">
                Get My Quote
              </Link>
            </div>

            {/* Mobile */}
            <div className="flex items-center gap-3 lg:hidden">
              <Link to="/contact" className="btn-cta text-xs px-4 py-2.5 hidden sm:inline-block" onClick={() => setIsOpen(false)} data-testid="mobile-header-quote-button">
                Get My Quote
              </Link>
              <button className="p-2 text-white hover:bg-white/5 rounded-lg transition-colors" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-button" aria-label="Toggle menu">
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="lg:hidden overflow-hidden border-t border-white/10 bg-brand-navy/95 backdrop-blur-xl" data-testid="mobile-menu">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-1">
                <Link to="/contact" className="btn-cta text-center block mb-5 py-3.5 sm:hidden" onClick={() => setIsOpen(false)} data-testid="mobile-nav-quote-button">
                  Get My Quote
                </Link>
                {navLinks.map((link) => (
                  <Link key={link.path} to={link.path}
                    className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${isActive(link.path) ? 'text-white bg-white/5' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                    onClick={() => setIsOpen(false)} data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}-link`}>
                    {link.name}
                  </Link>
                ))}
                <div data-testid="mobile-company-dropdown">
                  <button onClick={() => setMobileCompanyOpen(prev => !prev)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium transition-colors ${isCompanyActive ? 'text-white bg-white/5' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                    data-testid="mobile-company-trigger">
                    About Us
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileCompanyOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {mobileCompanyOpen && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                        <div className="pl-4 space-y-1 mt-1">
                          {companyLinks.map((link) => (
                            <Link key={link.path} to={link.path}
                              className={`block px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive(link.path) ? 'text-white bg-white/5' : 'text-slate-500 hover:text-white hover:bg-white/5'}`}
                              onClick={() => setIsOpen(false)} data-testid={`mobile-nav-company-${link.name.toLowerCase().replace(/\s+/g, '-')}-link`}>
                              {link.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <Link to="/contact"
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${isActive('/contact') ? 'text-white bg-white/5' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                  onClick={() => setIsOpen(false)} data-testid="mobile-nav-contact-link">
                  Contact
                </Link>
                <div className="pt-5 mt-4 border-t border-white/10">
                  <a href="tel:224-374-7400" className="flex items-center gap-3 px-4 text-slate-500 hover:text-white transition-colors text-sm">
                    <Phone className="w-4 h-4 text-brand-red/70" /><span>224-374-7400</span>
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Trust Strip */}
      <div className="bg-brand-surface/40 border-b border-white/5" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-6 sm:gap-10 py-2.5 overflow-x-auto">
            {trustItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 flex-shrink-0" data-testid={`trust-item-${i}`}>
                <item.icon className="w-3.5 h-3.5 text-brand-red/60" />
                <span className="text-slate-500 text-xs font-medium whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
